import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import { day } from '../../lib/when';

/*
  The three count reports: what they read, what they add up, and what their file
  carries.

  **Built 6 September 2026 to Group F's approved drawings**, after the owner
  looked at two shapes on 4 September and said "I have reviewed group f and I
  want shape B". Shape B is **a report that shows the rows it found**.

  What was there before: Count History, Non-Inventory Items and Dead Stock Items
  were one screen with one word changed, and none of the three reported
  anything. You chose filters, pressed Export CSV, and a file downloaded. There
  was no table, no total, no count of matching rows, and no way to tell whether
  the filters had caught anything at all before the file arrived.

  Everything in this file is separate from the screen so it can be tested,
  because this project has no way to render a component in a test.
*/

/*
  A line nobody has been to. **Rule 9: blank is not zero.** A shelf somebody
  walked to and found empty is a 0 and a real fact; a shelf nobody has walked to
  is a hole. The old file wrote both as an empty cell, which in Excel is
  indistinguishable from a zero, and 600 of Count History's 2,530 lines are
  holes.
*/
export function isBlank(line) {
  return line.quantity === null || line.quantity === undefined;
}

export const REPORTS = {
  count_history: {
    title: 'Count History',
    subtitle: 'Full count results for the cycles and places you pick',
    file: 'OnHand_Counts',
    banded: true,          // read down one building at a time
    blankLabel: 'Still blank',
  },
  non_inventory: {
    title: 'Non-Inventory Items',
    subtitle: 'Items flagged specialty, custom ordered or inherited',
    file: 'OnHand_NonInventory',
    column: 'is_non_inventory',
    banded: false,         // read across buildings
    blankLabel: 'Still blank',
  },
  dead_stock: {
    title: 'Dead Stock Items',
    subtitle: 'Items flagged dead stock or no longer in use',
    file: 'OnHand_DeadStock',
    column: 'is_dead_stock',
    banded: false,
    blankLabel: 'Still blank',
  },
};

const LINE_SELECT = `
  id, item_number_raw, description_raw, vendor_raw,
  quantity, previous_quantity, not_in_catalog,
  was_edited_after_submit, is_new_item, entered_via_scan,
  location, location_seq,
  count:inventory_counts!inner(
    id, cycle_id, submitted_at, approved_at, status,
    rep:profiles!inventory_counts_rep_id_fkey(full_name, email),
    warehouse:warehouses(id, name, display_name, region_prefix, region:regions(name))
  )
`;

/*
  **Read a page at a time.** A request brings back about a thousand rows and says
  nothing about the rest, so a cycle holding 3,250 lines used to produce a file
  with a confident row count on it and most of the count missing.

  **And `!inner` on the join is what makes the cycle filter filter.** Naming a
  joined table in a filter narrows the JOINED rows, not the ones being fetched,
  unless the join is marked that way. Without it the thousand rows that did
  arrive were the first thousand of every line in the site.
*/
export async function loadLines(kind, cycleIds) {
  const spec = REPORTS[kind];
  let q = supabase.from('count_line_items').select(LINE_SELECT);
  if (spec.column) q = q.eq(spec.column, true);
  if (cycleIds && cycleIds.length) q = q.in('count.cycle_id', cycleIds);
  const { data, error } = await fetchAll(() => q.order('id'));
  return { rows: (data || []).filter(r => r.count), error };
}

export function locationOf(line) {
  const w = line.count?.warehouse;
  return {
    id:   w?.id || '',
    code: w?.region_prefix || '',
    name: w?.display_name || w?.name || '',
    region: w?.region?.name || '',
  };
}

/*
  **One row per item per sheet**, which is what the file has always written and
  what a person means by "the count of that item at that location". A sheet can
  carry the same item on three shelves; the screen adds them up and the shelves
  are one of the columns the file carries and the screen does not.

  **Adding up must not turn a hole into a zero.** `(a || 0) + (b || 0)` was how
  the file did it, so an item nobody had counted came out as 0. If every entry
  for an item is blank, the rolled up line is blank.
*/
export function rollUp(lines) {
  const map = new Map();
  for (const line of lines) {
    const key = (line.count?.id || '') + '||' + (line.item_number_raw || '').toUpperCase();
    let row = map.get(key);
    if (!row) {
      // Both totals start empty. Copying the first line's own numbers and then
      // adding them again counted the first shelf twice: a line whose last
      // count was 35 read 70.
      row = { ...line, quantity: null, previous_quantity: null, shelves: [] };
      map.set(key, row);
    }
    row.shelves.push(line);
    if (!isBlank(line)) row.quantity = (row.quantity || 0) + line.quantity;
    if (line.previous_quantity !== null && line.previous_quantity !== undefined) {
      row.previous_quantity = (row.previous_quantity || 0) + line.previous_quantity;
    }
  }
  return [...map.values()];
}

/* The shelves an item was counted on, as the file writes them: "SHELF A (3), BACK ROOM (2)". */
export function shelfList(row) {
  return (row.shelves || [])
    .filter(e => e.location)
    .sort((a, b) => (a.location_seq || 1) - (b.location_seq || 1))
    .map(e => e.location + ' (' + (e.quantity || 0) + ')')
    .join(', ');
}

/*
  The strip of numbers above the rows. Rule 5: numbers on hairlines, and **how
  many are still blank is one of them**, in red, because it is the number that
  decides whether the file is worth anything.
*/
export function summarise(rows) {
  const locations = new Set();
  const items = new Set();
  let units = 0;
  let blank = 0;
  for (const r of rows) {
    const loc = locationOf(r);
    if (loc.id) locations.add(loc.id);
    if (r.item_number_raw) items.add(r.item_number_raw.toUpperCase());
    if (isBlank(r)) blank += 1; else units += r.quantity;
  }
  return { lines: rows.length, locations: locations.size, items: items.size, units, blank };
}

/* Location then item number, which is the order both the screen and the file use. */
export function inReadingOrder(rows) {
  return [...rows].sort((a, b) => {
    const la = locationOf(a), lb = locationOf(b);
    if (la.name !== lb.name) return la.name < lb.name ? -1 : 1;
    const ia = (a.item_number_raw || ''), ib = (b.item_number_raw || '');
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
}

/* Count History is read down one building at a time, so its rows come in bands. */
export function bandsOf(rows) {
  const out = [];
  let current = null;
  for (const r of inReadingOrder(rows)) {
    const loc = locationOf(r);
    if (!current || current.id !== loc.id) {
      current = { ...loc, rows: [], sheet: r.count };
      out.push(current);
    }
    current.rows.push(r);
  }
  return out;
}

export function matchesSearch(row, search) {
  const q = (search || '').trim().toLowerCase();
  if (!q) return true;
  const loc = locationOf(row);
  return [row.item_number_raw, row.description_raw, row.vendor_raw, loc.name, loc.code]
    .some(v => (v || '').toLowerCase().includes(q));
}

export function applyFilters(rows, { regionNames, locationIds, search }) {
  return rows.filter(r => {
    const loc = locationOf(r);
    if (regionNames?.length && !regionNames.includes(loc.region)) return false;
    if (locationIds?.length && !locationIds.includes(loc.id)) return false;
    return matchesSearch(r, search);
  });
}

/*
  **What the button says it will do.** "Export 2,530 rows", not "Export CSV".
  The same button doing the same thing, now saying what it will do, and the
  number is the number of rows the file will have because the file is built from
  exactly the rows on the screen.
*/
export function exportLabel(n) {
  return 'Export ' + n.toLocaleString() + ' row' + (n === 1 ? '' : 's');
}

const COUNT_HEADER = [
  'Region', 'Location', 'Rep', 'Rep Email',
  'Item Number', 'Description', 'Vendor',
  'Count', 'Previous Count',
  'Locations',
  'New Item', 'Not In Catalog', 'Edited After Submit', 'Scanned',
  'Submitted Date', 'Approved Date', 'Status',
];

const FLAG_HEADER = ['Region', 'Location', 'Rep', 'Item Number', 'Description',
  'Vendor', 'Quantity', 'Status', 'Submitted Date'];

/*
  **A blank line writes the words rather than an empty cell.** Rule 9 again, and
  it is the change with the most consequence in this whole file: 600 of the
  2,530 lines Count History matches today have never been counted, and every one
  of them used to arrive in Excel looking exactly like a zero.
*/
function csvQuantity(row) {
  return isBlank(row) ? 'NOT COUNTED' : row.quantity;
}

export function csvFor(kind, rows) {
  const ordered = inReadingOrder(rows);
  if (kind === 'count_history') {
    return [COUNT_HEADER, ...ordered.map(r => [
      locationOf(r).region,
      locationOf(r).name,
      r.count?.rep?.full_name || '',
      r.count?.rep?.email || '',
      r.item_number_raw || '',
      r.description_raw || '',
      r.vendor_raw || '',
      csvQuantity(r),
      r.previous_quantity ?? '',
      shelfList(r),
      r.is_new_item ? 'YES' : 'NO',
      r.not_in_catalog ? 'FLAG' : 'NO',
      r.was_edited_after_submit ? 'FLAG' : 'NO',
      r.entered_via_scan ? 'YES' : 'NO',
      r.count?.submitted_at ? day(r.count.submitted_at) : '',
      r.count?.approved_at ? day(r.count.approved_at) : '',
      r.count?.status || '',
    ])];
  }
  return [FLAG_HEADER, ...ordered.map(r => [
    locationOf(r).region,
    locationOf(r).name,
    r.count?.rep?.full_name || '',
    r.item_number_raw || '',
    r.description_raw || '',
    r.vendor_raw || '',
    csvQuantity(r),
    r.count?.status || '',
    r.count?.submitted_at ? day(r.count.submitted_at) : '',
  ])];
}

export function downloadCsv(rows, filename) {
  const csv = rows.map(r => r.map(v => '"' + String(v ?? '').replace(/"/g, '""') + '"').join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/*
  **How many rows the screen draws before it stops.** The drawings carry the
  sentence for it, "and 2,516 more lines, across 22 more locations", so the cap
  is the design rather than a shortcut. Everything the filters matched still
  goes in the file: the cap is about a browser drawing rows, not about what the
  report found.
*/
export const DRAW_CAP = 500;

export function moreSentence(kind, shownRows, shownLocations, total) {
  const left = total.lines - shownRows;
  if (left <= 0) return null;
  const words = 'and ' + left.toLocaleString() + ' more line' + (left === 1 ? '' : 's');
  if (kind !== 'count_history') return words;
  const locsLeft = total.locations - shownLocations;
  if (locsLeft <= 0) return words;
  return words + ', across ' + locsLeft.toLocaleString() + ' more location' + (locsLeft === 1 ? '' : 's');
}
